import React, { useState, useEffect } from "react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Switch,
} from "@headlessui/react";
import {
  ChevronDownIcon,
  AlertCircle,
  X,
  Plus,
  CheckCircle2,
  Loader2,
  Shield,
  Search,
  Trash2
} from "lucide-react";
import { getGuardrailsSettings, updateGuardrailsSettings } from "../../../../service/apiService";

import type {
  GuardrailSettings,
  PIIEntity,
  RegexPattern,
  SensitiveInfoSettings
} from "../../../../types";

// COMPLETE PII TYPES LIST
const AVAILABLE_PII_TYPES = [
  // General Category
  { value: 'NAME', label: 'Name', category: 'General' },
  { value: 'PHONE', label: 'Phone', category: 'General' },
  { value: 'EMAIL', label: 'Email', category: 'General' },
  { value: 'ADDRESS', label: 'Address', category: 'General' },
  { value: 'AGE', label: 'Age', category: 'General' },
  { value: 'USERNAME', label: 'Username', category: 'General' },
  { value: 'PASSWORD', label: 'Password', category: 'General' },
  { value: 'LICENSE_PLATE', label: 'License plate', category: 'General' },
  { value: 'VEHICLE_IDENTIFICATION_NUMBER', label: 'Vehicle Identification number (VIN)', category: 'General' },

  // Finance Category
  // { value: 'CVV', label: 'CVV', category: 'Finance' },
  // { value: 'PERSONAL_IDENTIFICATION_NUMBER', label: 'Personal identification number (PIN)', category: 'Finance' },
  { value: 'CREDIT_DEBIT_CARD_CVV', label: 'CVV', category: 'Finance' },
  { value: 'PIN', label: 'Personal identification number (PIN)', category: 'Finance' },
  { value: 'SWIFT_CODE', label: 'SWIFT code', category: 'Finance' },
  { value: 'CREDIT_DEBIT_CARD_NUMBER', label: 'Credit/Debit Card Number', category: 'Finance' },
  { value: 'CREDIT_DEBIT_CARD_EXPIRY', label: 'Credit Card Expiry', category: 'Finance' },
  { value: 'INTERNATIONAL_BANK_ACCOUNT_NUMBER', label: 'International Bank Account Number', category: 'Finance' },

  // IT Category
  { value: 'IP_ADDRESS', label: 'IPv4 address', category: 'IT' },
  { value: 'MAC_ADDRESS', label: 'Media access control (MAC) address', category: 'IT' },
  // { value: 'WEB_ADDRESS', label: 'Web address', category: 'IT' },
  { value: 'URL', label: 'Web address (URL)', category: 'IT' },
  { value: 'AWS_ACCESS_KEY', label: 'AWS access key', category: 'IT' },
  { value: 'AWS_SECRET_KEY', label: 'AWS secret key', category: 'IT' },

  // USA Specific
  // { value: 'US_SOCIAL_SECURITY_NUMBER', label: 'US Social Security Number (SSN)', category: 'USA Specific' },
  { value: 'US_SOCIAL_SECURITY_NUMBER', label: 'US Social Security Number (SSN)', category: 'USA Specific' },
  { value: 'US_INDIVIDUAL_TAX_IDENTIFICATION_NUMBER', label: 'US Individual Taxpayer Identification Number (ITIN)', category: 'USA Specific' },
  { value: 'US_BANK_ACCOUNT_NUMBER', label: 'US bank account number', category: 'USA Specific' },
  { value: 'US_BANK_ROUTING_NUMBER', label: 'US bank account routing number', category: 'USA Specific' },
  { value: 'US_PASSPORT_NUMBER', label: 'US Passport Number', category: 'USA Specific' },
  { value: 'DRIVER_ID', label: 'Driver ID', category: 'USA Specific' },

  // Canada Specific
  { value: 'CA_HEALTH_NUMBER', label: 'Canadian Health Service Number', category: 'Canada Specific' },
  { value: 'CA_SOCIAL_INSURANCE_NUMBER', label: 'Canadian Social Insurance Number (SIN)', category: 'Canada Specific' },

  // UK Specific
  { value: 'UK_UNIQUE_TAXPAYER_REFERENCE_NUMBER', label: 'UK Unique Taxpayer Reference (UTR)', category: 'UK Specific' },
  { value: 'UK_NATIONAL_INSURANCE_NUMBER', label: 'UK National Insurance Number (NINO)', category: 'UK Specific' },
  { value: 'UK_NATIONAL_HEALTH_SERVICE_NUMBER', label: 'UK National Health Service Number', category: 'UK Specific' },
];

interface PromptProps {
  restrictedWords?: string[];
  handleUpdateRestrictedWords?: (words: string[]) => void;
}

const Prompt: React.FC<PromptProps> = ({
  restrictedWords = [],
  handleUpdateRestrictedWords,
}) => {
  // ALL STATE VARIABLES
  const [isEditing, setIsEditing] = useState(false);
  const [newWord, setNewWord] = useState("");
  const [wordsList, setWordsList] = useState<string[]>([]);
  const [guardrailsEnabled, setGuardrailsEnabled] = useState(false);
  const [fileScanEnabled, setFileScanEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currentSettings, setCurrentSettings] = useState<GuardrailSettings>({
    enabled: false,
    file_scan_enabled: false,
    restricted_words: [],
    sensitiveInfo: {
      piiEntities: [],
      regexPatterns: []
    }
  });

  // PII State
  const [selectedPIITypes, setSelectedPIITypes] = useState<PIIEntity[]>([]);
  const [showPIISelector, setShowPIISelector] = useState(false);
  const [piiSearchTerm, setPIISearchTerm] = useState("");

  // Regex State
  const [regexPatterns, setRegexPatterns] = useState<RegexPattern[]>([]);
  const [showRegexForm, setShowRegexForm] = useState(false);
  const [newRegex, setNewRegex] = useState<RegexPattern>({
    name: '',
    pattern: '',
    inputAction: 'BLOCK',
    outputAction: 'BLOCK',
    inputEnabled: true,
    outputEnabled: true,
    description: ''
  });

  // Load settings on mount
  useEffect(() => {
    loadCurrentSettings();
  }, []);


  const loadCurrentSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getGuardrailsSettings();

      setCurrentSettings(response.data);
      setGuardrailsEnabled(response.data.enabled);
      setFileScanEnabled(response.data.file_scan_enabled || false);
      setWordsList(response.data.restricted_words || []);

      if (response.data.sensitiveInfo) {
        setSelectedPIITypes(response.data.sensitiveInfo.piiEntities || []);
        setRegexPatterns(response.data.sensitiveInfo.regexPatterns || []);
      }

      setSuccess(`Loaded settings successfully`);
      setTimeout(() => setSuccess(null), 3000);

    } catch (err) {
      console.error("Failed to load guardrails settings:", err);
      setError("Failed to load settings. Please try again.");

      if (restrictedWords.length > 0) {
        setWordsList(restrictedWords);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
    setSuccess(null);
  };

  const handleAddWord = () => {
    const trimmedWord = newWord.trim().toLowerCase();

    if (!trimmedWord) {
      setError("Please enter a word");
      return;
    }

    if (wordsList.includes(trimmedWord)) {
      setError(`"${trimmedWord}" is already in the list`);
      return;
    }

    setWordsList([...wordsList, trimmedWord]);
    setNewWord("");
    setError(null);
  };

  const handleRemoveWord = (wordToRemove: string) => {
    setWordsList(wordsList.filter(word => word !== wordToRemove));
    setError(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddWord();
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const newSettings: GuardrailSettings = {
        enabled: guardrailsEnabled,
        file_scan_enabled: fileScanEnabled,
        restricted_words: wordsList,
        sensitiveInfo: {
          piiEntities: selectedPIITypes,
          regexPatterns: regexPatterns
        }
      };

      const response = await updateGuardrailsSettings(newSettings);

      setCurrentSettings(newSettings);
      setIsEditing(false);

      setSuccess(`Successfully saved! Words: ${wordsList.length}, PII: ${selectedPIITypes.length}, Regex: ${regexPatterns.length}`);
      setTimeout(() => setSuccess(null), 5000);

      if (handleUpdateRestrictedWords) {
        handleUpdateRestrictedWords(wordsList);
      }

    } catch (err: any) {
      console.error("Failed to save guardrails settings:", err);
      setError(err.response?.data?.detail || "Failed to save settings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setWordsList(currentSettings.restricted_words || []);
    setGuardrailsEnabled(currentSettings.enabled);
    setFileScanEnabled(currentSettings.file_scan_enabled || false);
    setSelectedPIITypes(currentSettings.sensitiveInfo?.piiEntities || []);
    setRegexPatterns(currentSettings.sensitiveInfo?.regexPatterns || []);
    setNewWord("");
    setIsEditing(false);
    setError(null);
    setSuccess(null);
  };

  // PII Functions
  const filteredPIITypes = AVAILABLE_PII_TYPES.filter(pii =>
    !selectedPIITypes.some(selected => selected.type === pii.value) &&
    (pii.label.toLowerCase().includes(piiSearchTerm.toLowerCase()) ||
      pii.category.toLowerCase().includes(piiSearchTerm.toLowerCase()))
  );

  const groupedPIITypes = filteredPIITypes.reduce((acc, pii) => {
    if (!acc[pii.category]) acc[pii.category] = [];
    acc[pii.category].push(pii);
    return acc;
  }, {} as Record<string, typeof AVAILABLE_PII_TYPES>);

  const handleAddPII = (piiType: string) => {
    const newPII: PIIEntity = {
      type: piiType,
      inputAction: 'BLOCK',
      outputAction: 'BLOCK',
      inputEnabled: true,
      outputEnabled: true
    };
    setSelectedPIITypes([...selectedPIITypes, newPII]);
    setPIISearchTerm("");
    setShowPIISelector(false);
  };

  const handleRemovePII = (piiType: string) => {
    setSelectedPIITypes(selectedPIITypes.filter(pii => pii.type !== piiType));
  };

  const updatePIIConfig = (piiType: string, updates: Partial<PIIEntity>) => {
    setSelectedPIITypes(selectedPIITypes.map(pii =>
      pii.type === piiType ? { ...pii, ...updates } : pii
    ));
  };

  // Regex Functions
  const handleAddRegex = () => {
    if (!newRegex.name.trim() || !newRegex.pattern.trim()) {
      setError("Please provide both name and pattern for the regex");
      return;
    }

    setRegexPatterns([...regexPatterns, { ...newRegex }]);
    setNewRegex({
      name: '',
      pattern: '',
      inputAction: 'BLOCK',
      outputAction: 'BLOCK',
      inputEnabled: true,
      outputEnabled: true,
      description: ''
    });
    setShowRegexForm(false);
    setError(null);
  };

  const handleRemoveRegex = (index: number) => {
    setRegexPatterns(regexPatterns.filter((_, i) => i !== index));
  };

  const getPIIDisplayName = (piiType: string) => {
    const piiInfo = AVAILABLE_PII_TYPES.find(p => p.value === piiType);
    return piiInfo?.label || piiType;
  };

  const getPIICategory = (piiType: string) => {
    const piiInfo = AVAILABLE_PII_TYPES.find(p => p.value === piiType);
    return piiInfo?.category || 'Unknown';
  };

  return (
    <Disclosure as="div" className="py-2">
      <DisclosureButton className="group flex w-full items-center justify-between">
        <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
          Advanced Security & Privacy (Guardrails)
        </span>
        <ChevronDownIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-300 group-open:rotate-180" />
      </DisclosureButton>

      <DisclosurePanel className="mt-2 text-sm mx-2 p-4 rounded-lg bg-white border border-gray-200">
        {/* Messages */}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center">
            <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
            <span className="text-green-700 text-sm">{success}</span>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center">
            <AlertCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0" />
            <span className="text-red-700 text-sm">{error}</span>
          </div>
        )}

        {loading && !isEditing && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-center">
            <Loader2 className="h-4 w-4 text-blue-500 mr-2 animate-spin" />
            <span className="text-blue-700 text-sm">Loading settings...</span>
          </div>
        )}

        {/* Toggles */}
        {isEditing && (
          <div className="mb-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-700">Enable Prompt Guardrails</label>
                <p className="text-xs text-gray-500">Scan conversational input and output for prohibited content.</p>
              </div>
              <input
                type="checkbox"
                checked={guardrailsEnabled}
                onChange={(e) => setGuardrailsEnabled(e.target.checked)}
                className="h-5 w-5 text-violet-600 rounded border-gray-300 focus:ring-violet-500"
                disabled={loading}
              />
            </div>
            <div className="flex items-center justify-between border-t border-gray-100 pt-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Enable File Upload Scanning</label>
                <p className="text-xs text-gray-500">Automatically scan all uploaded files for PII and restricted content.</p>
              </div>
              <input
                type="checkbox"
                checked={fileScanEnabled}
                onChange={(e) => setFileScanEnabled(e.target.checked)}
                className="h-5 w-5 text-violet-600 rounded border-gray-300 focus:ring-violet-500"
                disabled={loading}
              />
            </div>
          </div>
        )}

        {/* Word Input */}
        {isEditing && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Add Restricted Word
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newWord}
                onChange={(e) => setNewWord(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter a word to restrict"
                className="flex-1 px-3 py-2 bg-white text-gray-900 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none"
                disabled={loading}
              />
              <button
                onClick={handleAddWord}
                disabled={loading || !newWord.trim()}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </button>
            </div>
          </div>
        )}

        {/* Words List */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Restricted Words ({wordsList.length})
          </label>

          {wordsList.length === 0 ? (
            <div className="text-center py-8 px-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-sm text-gray-500">No restricted words added</p>
            </div>
          ) : (
            <div className="max-h-60 overflow-y-auto bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="flex flex-wrap gap-2">
                {wordsList.map((word, index) => (
                  <div
                    key={index}
                    className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-violet-100 text-violet-800 border border-violet-200"
                  >
                    <span>{word}</span>
                    {isEditing && (
                      <button
                        onClick={() => handleRemoveWord(word)}
                        disabled={loading}
                        className="ml-2 inline-flex items-center justify-center h-4 w-4 rounded-full text-violet-600 hover:bg-violet-200 hover:text-violet-800 transition-colors"
                        aria-label={`Remove ${word}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* PII Protection Section */}
        {isEditing && (
          <>
            <div className="mb-6 mt-6 pt-6 border-t border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-violet-600" />
                  <label className="block text-sm font-medium text-gray-700">
                    PII Protection ({selectedPIITypes.length})
                  </label>
                </div>
                <button
                  onClick={() => setShowPIISelector(true)}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded-md text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add PII Type
                </button>
              </div>

              {/* PII Types List */}
              {selectedPIITypes.length === 0 ? (
                <div className="text-center py-6 px-4 bg-violet-50 rounded-lg border-2 border-dashed border-violet-300">
                  <Shield className="h-8 w-8 text-violet-400 mx-auto mb-2" />
                  <p className="text-sm text-violet-600 font-medium">No PII protection configured</p>
                  <p className="text-xs text-violet-500 mt-1">Add PII types to protect sensitive information like SSN, credit cards, etc.</p>
                </div>
              ) : (
                <div className="bg-violet-50 rounded-lg p-4 border border-violet-200">
                  <div className="space-y-3">
                    {selectedPIITypes.map((pii) => (
                      <div key={pii.type} className="bg-white p-4 rounded-md border border-violet-200 shadow-sm">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-700">{getPIIDisplayName(pii.type)}</span>
                              <span className="text-xs text-violet-600 bg-violet-100 px-2 py-0.5 rounded-full">
                                {getPIICategory(pii.type)}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{pii.type}</p>
                          </div>
                          <button
                            onClick={() => handleRemovePII(pii.type)}
                            className="text-red-600 hover:text-red-800 p-1"
                            aria-label={`Remove ${getPIIDisplayName(pii.type)}`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>

                        {/* PII Configuration Grid */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={pii.inputEnabled}
                                onChange={(e) => updatePIIConfig(pii.type, { inputEnabled: e.target.checked })}
                                className="h-4 w-4 text-violet-600 rounded border-gray-300"
                              />
                              <span className="text-xs font-medium text-gray-700">Input Protection</span>
                            </div>
                            {pii.inputEnabled && (
                              <select
                                value={pii.inputAction}
                                onChange={(e) => updatePIIConfig(pii.type, { inputAction: e.target.value as 'BLOCK' | 'ANONYMIZE' })}
                                className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:ring-1 focus:ring-violet-500"
                              >
                                <option value="BLOCK">Block</option>
                                <option value="ANONYMIZE">Anonymize</option>
                              </select>
                            )}
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={pii.outputEnabled}
                                onChange={(e) => updatePIIConfig(pii.type, { outputEnabled: e.target.checked })}
                                className="h-4 w-4 text-violet-600 rounded border-gray-300"
                              />
                              <span className="text-xs font-medium text-gray-700">Output Protection</span>
                            </div>
                            {pii.outputEnabled && (
                              <select
                                value={pii.outputAction}
                                onChange={(e) => updatePIIConfig(pii.type, { outputAction: e.target.value as 'BLOCK' | 'ANONYMIZE' })}
                                className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:ring-1 focus:ring-violet-500"
                              >
                                <option value="BLOCK">Block</option>
                                <option value="ANONYMIZE">Anonymize</option>
                              </select>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Custom Regex Patterns Section */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Custom Regex Patterns ({regexPatterns.length})
                </label>
                <button
                  onClick={() => setShowRegexForm(true)}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded-md text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Regex Pattern
                </button>
              </div>

              {/* Regex Patterns List */}
              {regexPatterns.length === 0 ? (
                <div className="text-center py-6 px-4 bg-violet-50 rounded-lg border-2 border-dashed border-violet-300">
                  <p className="text-sm text-violet-600 font-medium">No custom patterns</p>
                  <p className="text-xs text-violet-500 mt-1">Add regex patterns for organization-specific sensitive data</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {regexPatterns.map((regex, index) => (
                    <div key={index} className="bg-white p-4 rounded-md border border-violet-200 shadow-sm">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <div className="font-medium text-sm text-gray-700">{regex.name}</div>
                          <div className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded mt-1 break-all">
                            {regex.pattern}
                          </div>
                          {regex.description && (
                            <div className="text-xs text-gray-500 mt-1">{regex.description}</div>
                          )}
                        </div>
                        <button
                          onClick={() => handleRemoveRegex(index)}
                          className="text-red-600 hover:text-red-800 p-1 ml-2"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Regex Configuration Grid */}
                      <div className="grid grid-cols-2 gap-4 mt-3">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={regex.inputEnabled}
                              onChange={(e) => {
                                const updatedPatterns = [...regexPatterns];
                                updatedPatterns[index] = { ...regex, inputEnabled: e.target.checked };
                                setRegexPatterns(updatedPatterns);
                              }}
                              className="h-4 w-4 text-violet-600 rounded border-gray-300"
                            />
                            <span className="text-xs font-medium text-gray-700">Input Protection</span>
                          </div>
                          {regex.inputEnabled && (
                            <select
                              value={regex.inputAction}
                              onChange={(e) => {
                                const updatedPatterns = [...regexPatterns];
                                updatedPatterns[index] = { ...regex, inputAction: e.target.value as 'BLOCK' | 'ANONYMIZE' };
                                setRegexPatterns(updatedPatterns);
                              }}
                              className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:ring-1 focus:ring-violet-500"
                            >
                              <option value="BLOCK">Block</option>
                              <option value="ANONYMIZE">Anonymize</option>
                            </select>
                          )}
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={regex.outputEnabled}
                              onChange={(e) => {
                                const updatedPatterns = [...regexPatterns];
                                updatedPatterns[index] = { ...regex, outputEnabled: e.target.checked };
                                setRegexPatterns(updatedPatterns);
                              }}
                              className="h-4 w-4 text-violet-600 rounded border-gray-300"
                            />
                            <span className="text-xs font-medium text-gray-700">Output Protection</span>
                          </div>
                          {regex.outputEnabled && (
                            <select
                              value={regex.outputAction}
                              onChange={(e) => {
                                const updatedPatterns = [...regexPatterns];
                                updatedPatterns[index] = { ...regex, outputAction: e.target.value as 'BLOCK' | 'ANONYMIZE' };
                                setRegexPatterns(updatedPatterns);
                              }}
                              className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:ring-1 focus:ring-violet-500"
                            >
                              <option value="BLOCK">Block</option>
                              <option value="ANONYMIZE">Anonymize</option>
                            </select>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
        {/* PII Selector Modal */}
        {showPIISelector && (
          <div className="fixed inset-0 scrim flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[80vh] overflow-hidden">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Add PII Protection</h3>
                <button
                  onClick={() => setShowPIISelector(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Search Bar */}
              <div className="mb-4 relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search PII types (e.g., credit card, SSN, email)..."
                  value={piiSearchTerm}
                  onChange={(e) => setPIISearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-md focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                />
              </div>

              {/* Grouped PII Types */}
              <div className="max-h-96 overflow-y-auto">
                {Object.entries(groupedPIITypes).map(([category, piiTypes]) => (
                  <div key={category} className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2 bg-gray-50 px-3 py-2 rounded-md border-l-4 border-violet-500">
                      {category} ({piiTypes.length})
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-2">
                      {piiTypes.map((pii) => (
                        <button
                          key={pii.value}
                          onClick={() => handleAddPII(pii.value)}
                          className="text-left px-3 py-2 text-sm hover:bg-violet-50 rounded-md border border-transparent hover:border-violet-200 transition-colors"
                        >
                          <div className="font-medium text-gray-700">{pii.label}</div>
                          <div className="text-xs text-gray-500">{pii.value}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {Object.keys(groupedPIITypes).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Search className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>No PII types found matching "{piiSearchTerm}"</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Regex Form Modal */}
        {showRegexForm && (
          <div className="fixed inset-0 scrim flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Add Custom Regex Pattern</h3>
                <button
                  onClick={() => setShowRegexForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Pattern Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pattern Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Company Employee ID"
                    value={newRegex.name}
                    onChange={(e) => setNewRegex({ ...newRegex, name: e.target.value })}
                    className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-md focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                  />
                </div>

                {/* Regex Pattern */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Regex Pattern <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., ^EMP[0-9]{6}$"
                    value={newRegex.pattern}
                    onChange={(e) => setNewRegex({ ...newRegex, pattern: e.target.value })}
                    className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-md focus:ring-2 focus:ring-violet-500 focus:border-violet-500 font-mono text-sm"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (Optional)
                  </label>
                  <textarea
                    placeholder="Describe what this pattern detects..."
                    value={newRegex.description}
                    onChange={(e) => setNewRegex({ ...newRegex, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-md focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                  />
                </div>

                {/* Action Configuration */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Input Action</label>
                    <select
                      value={newRegex.inputAction}
                      onChange={(e) => setNewRegex({ ...newRegex, inputAction: e.target.value as 'BLOCK' | 'ANONYMIZE' })}
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-violet-500"
                    >
                      <option value="BLOCK">Block</option>
                      <option value="ANONYMIZE">Anonymize</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Output Action</label>
                    <select
                      value={newRegex.outputAction}
                      onChange={(e) => setNewRegex({ ...newRegex, outputAction: e.target.value as 'BLOCK' | 'ANONYMIZE' })}
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-violet-500"
                    >
                      <option value="BLOCK">Block</option>
                      <option value="ANONYMIZE">Anonymize</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowRegexForm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddRegex}
                  disabled={!newRegex.name.trim() || !newRegex.pattern.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-violet-600 border border-transparent rounded-md hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Pattern
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Action Buttons */}
        <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
          {isEditing ? (
            <>
              <button
                onClick={handleCancel}
                disabled={loading}
                className="inline-flex justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </>
          ) : (
            <button
              onClick={handleEdit}
              disabled={loading}
              className="inline-flex justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? "Loading..." : "Edit Settings"}
            </button>
          )}
        </div>

        {/* Info Footer */}
        <div className="mt-4 pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            💡 <strong>Tip:</strong> All security settings are stored in AWS Bedrock Working Draft and synchronized in real-time.
            Changes include restricted words, PII protection, and custom regex patterns.
          </p>
          {guardrailsEnabled && (
            <div className="mt-2 text-xs text-gray-600">
              <div className="flex items-center gap-4">
                <span>📝 Words: {wordsList.length}</span>
                <span>🛡️ PII Types: {selectedPIITypes.length}</span>
                <span>🔍 Regex Patterns: {regexPatterns.length}</span>
              </div>
            </div>
          )}
        </div>
      </DisclosurePanel>
    </Disclosure>
  );
};

export default Prompt;